/*
 * EV Energy Compare — a 7-step wizard that estimates EV charging cost on the user's
 * current rate vs. the best-matching EV energy plan, plus a searchable plans table.
 *
 * Vehicle data: assets/ev-vehicles.json (shared with the Charge calculator).
 * Plan data:    assets/ev-plans.json   (generated from plans.html by build-plans.js).
 *
 * Cost model (see "How this was calculated"):
 *   weeklyKwh   = weeklyKm / efficiency
 *   currentCost = weeklyKwh * currentRate
 *   per plan:   cheapKwh   = min(weeklyKwh, cheapHoursPerWeek * chargerKw)
 *               overflow   = weeklyKwh - cheapKwh   (billed at currentRate)
 *               weeklyCost = cheapKwh * cheapRate + overflow * currentRate
 *   With solar on a daytime plan, solar-charged kWh cost the feed-in tariff (opportunity cost).
 */

const STATE_RANGES = [
  ['ACT', (n) => (n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)],
  ['NSW', (n) => (n >= 1000 && n <= 1999) || (n >= 2000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)],
  ['NT', (n) => n >= 800 && n <= 999],
  ['VIC', (n) => (n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)],
  ['QLD', (n) => (n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)],
  ['SA', (n) => n >= 5000 && n <= 5999],
  ['WA', (n) => n >= 6000 && n <= 6999],
  ['TAS', (n) => n >= 7000 && n <= 7999],
];

function stateFromPostcode(pc) {
  const n = parseInt(pc, 10);
  if (!Number.isFinite(n)) return '';
  for (const [code, test] of STATE_RANGES) if (test(n)) return code;
  return '';
}

// Approximate primary distributor (network) by state + postcode band.
function networkFor(pc, state) {
  const n = parseInt(pc, 10);
  switch (state) {
    case 'NSW': {
      // Endeavour Energy: Greater Western Sydney + Hills, Macarthur, Southern Highlands,
      // Blue Mountains/Hawkesbury/Penrith, Illawarra & Shoalhaven.
      const endeavour =
        (n >= 2145 && n <= 2179) ||
        (n >= 2555 && n <= 2580) ||
        (n >= 2745 && n <= 2786) ||
        (n >= 2500 && n <= 2535) ||
        n === 2540;
      if (endeavour) return 'Endeavour Energy';
      // Ausgrid: Sydney core (CBD/East/Inner West/North/South), Central Coast, Newcastle/Hunter.
      const ausgrid =
        (n >= 2000 && n <= 2144) ||
        (n >= 2180 && n <= 2234) ||
        (n >= 2250 && n <= 2265) ||
        (n >= 2280 && n <= 2334);
      if (ausgrid) return 'Ausgrid';
      return 'Essential Energy'; // regional / rural NSW
    }
    case 'ACT': return 'Evoenergy';
    case 'VIC': return n >= 3000 && n <= 3207 ? 'CitiPower / Jemena' : 'Powercor / AusNet';
    case 'QLD': return n >= 4000 && n <= 4207 ? 'Energex' : 'Ergon Energy';
    case 'SA': return 'SA Power Networks';
    case 'WA': return n >= 6000 && n <= 6199 ? 'Western Power' : 'Horizon Power';
    case 'TAS': return 'TasNetworks';
    case 'NT': return 'Power and Water';
    default: return '';
  }
}

const STATE_NAMES = { NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', SA: 'South Australia', WA: 'Western Australia', ACT: 'ACT', TAS: 'Tasmania', NT: 'Northern Territory' };

// Display names for the distributor codes used in postcode-lookup.json.
const DISTRIBUTOR_NAMES = {
  AUSGRID: 'Ausgrid',
  AUSNET: 'AusNet Services',
  CITIPOWER: 'CitiPower',
  ENDEAVOUR: 'Endeavour Energy',
  ENERGEX: 'Energex',
  ERGON: 'Ergon Energy',
  ESSENTIAL: 'Essential Energy',
  EVOENERGY: 'Evoenergy',
  JEMENA: 'Jemena',
  POWERCOR: 'Powercor',
  SAPN: 'SA Power Networks',
  TASNETWORKS: 'TasNetworks',
  UNITED: 'United Energy',
  'WESTERN POWER': 'Western Power',
};

/* ---- plan parsing (mirrors build-plans.js, for plans edited in the theme customizer) ---- */
function planHour(s) {
  const m = String(s).match(/(\d+)(?::(\d+))?\s*(am|pm)/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) / 60 : 0;
  const ap = m[3].toLowerCase();
  if (ap === 'pm' && h !== 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return h + min;
}
function planWindow(raw) {
  const out = { hoursPerWeek: null, overnightHours: 0, daytimeHours: 0, overnight: false, daytime: false, weekend: false };
  if (!raw) return out;
  out.weekend = /weekend/i.test(raw);
  const days = out.weekend ? 2 : 7;
  const ranges = [...raw.matchAll(/(\d+(?::\d+)?\s*[ap]m)\s*[-–]\s*(\d+(?::\d+)?\s*[ap]m)/gi)];
  if (!ranges.length) return out;
  let night = 0;
  let day = 0;
  for (const r of ranges) {
    const a = planHour(r[1]);
    const b = planHour(r[2]);
    if (a == null || b == null) continue;
    let dur = b - a;
    if (dur <= 0) dur += 24;
    const mid = (a + dur / 2) % 24;
    if (mid >= 20 || mid < 8) { night += dur; out.overnight = true; }
    else { day += dur; out.daytime = true; }
  }
  out.overnightHours = Math.round(night * days * 10) / 10;
  out.daytimeHours = Math.round(day * days * 10) / 10;
  out.hoursPerWeek = Math.round((night + day) * days * 10) / 10;
  return out;
}
function planRate(raw) {
  if (!raw) return null;
  if (/free/i.test(raw)) return 0;
  const m = String(raw).match(/(\d+(?:\.\d+)?)\s*c/i);
  return m ? parseFloat(m[1]) / 100 : null;
}
function planStates(raw) {
  if (!raw) return [];
  const out = [];
  ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'ACT', 'TAS', 'NT'].forEach((s) => {
    if (new RegExp('\\b' + s + '\\b').test(raw)) out.push(s);
  });
  return out;
}
function parsePlan(p) {
  const w = planWindow(p.window || '');
  return {
    provider: p.provider || '',
    plan: p.plan || '',
    link: p.link || null,
    rate: p.rate || '',
    cheapRate: planRate(p.rate),
    isFree: /free/i.test(p.rate || ''),
    window: p.window || '',
    cheapHoursPerWeek: w.hoursPerWeek,
    overnightHours: w.overnightHours,
    daytimeHours: w.daytimeHours,
    overnight: w.overnight,
    daytime: w.daytime,
    weekend: w.weekend,
    fit: p.fit || '',
    feedIn: planRate(p.fit),
    features: p.features || '',
    availability: p.availability || '',
    states: planStates(p.availability || ''),
    distributor: p.distributor || '',
    distributorCode: p.distributorCode || '',
  };
}
/* ---- external retailer-plans feed (optional, set via the theme customizer) ---- */
function hourLabel(h) {
  if (!Number.isFinite(h)) return null;
  const n = ((h % 24) + 24) % 24;
  let hr = n % 12;
  if (hr === 0) hr = 12;
  return `${hr}${n < 12 ? 'am' : 'pm'}`;
}
function externalPlanWindow(o) {
  if (o.optimisedForWeekendOnly && Number.isFinite(o.weekendWindowStart) && Number.isFinite(o.weekendWindowEnd)) {
    return `${hourLabel(o.weekendWindowStart)}-${hourLabel(o.weekendWindowEnd)} weekend`;
  }
  const parts = [];
  if (Number.isFinite(o.nightWindowStart) && Number.isFinite(o.nightWindowEnd)) parts.push(`${hourLabel(o.nightWindowStart)}-${hourLabel(o.nightWindowEnd)}`);
  if (Number.isFinite(o.dayWindowStart) && Number.isFinite(o.dayWindowEnd)) parts.push(`${hourLabel(o.dayWindowStart)}-${hourLabel(o.dayWindowEnd)}`);
  return parts.join(', ');
}
function externalPlanRate(o) {
  let rate = null;
  if (o.optimisedForWeekendOnly) rate = o.weekendRate;
  else if (o.optimisedForBothNightAndDay) rate = o.nightWindowRate != null ? o.nightWindowRate : o.dayWindowRate;
  else if (o.optimisedForDayCharging) rate = o.dayWindowRate;
  else if (o.optimisedForNightCharging) rate = o.nightWindowRate;
  if (rate == null) rate = o.nightWindowRate != null ? o.nightWindowRate : o.dayWindowRate != null ? o.dayWindowRate : o.weekendRate != null ? o.weekendRate : o.planFallbackRate;
  if (rate == null) return '';
  if (rate === 0) return 'Free';
  return `${(rate * 100).toFixed(2)}c/kWh`;
}
// Maps a retailer-plans.json entry onto the raw shape parsePlan() expects.
function externalPlanToRaw(o) {
  const features = [o.otherInfo || '', o.smartMeterRequired ? 'Requires a smart meter.' : ''].filter(Boolean).join(' ');
  return {
    provider: o.retailer || '',
    plan: o.planName || '',
    link: o.signupLink || null,
    rate: externalPlanRate(o),
    window: externalPlanWindow(o),
    fit: '',
    features,
    availability: o.state || '',
    distributor: DISTRIBUTOR_NAMES[o.distributor] || o.distributor || '',
    distributorCode: o.distributor || '',
  };
}
// Shared across every <ev-energy-compare>/<ev-plans-table> instance so the feed is fetched once per URL.
const _externalPlansCache = new Map();
function fetchExternalPlansRaw(url) {
  if (!_externalPlansCache.has(url)) {
    _externalPlansCache.set(
      url,
      fetch(url)
        .then((r) => r.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : Array.isArray(data && data.plans) ? data.plans : [];
          return list.map(externalPlanToRaw);
        })
        .catch((e) => { _externalPlansCache.delete(url); throw e; })
    );
  }
  return _externalPlansCache.get(url);
}
async function fetchExternalPlans(url) {
  return (await fetchExternalPlansRaw(url)).map(parsePlan);
}
// Reads raw plans entered as section blocks (in the customizer). Returns null if none.
function readEmbeddedPlans(el) {
  const scope = el.closest('.evc-scope') || document;
  const tag = scope.querySelector('[data-evc-plans]');
  if (!tag) return null;
  try {
    const arr = JSON.parse((tag.textContent || '').trim() || '[]');
    return Array.isArray(arr) && arr.length ? arr : null;
  } catch (e) {
    return null;
  }
}

class EvEnergyCompare extends HTMLElement {
  constructor() {
    super();
    this.efficiency = parseFloat(this.dataset.efficiency) || 5.5;
    this.currency = this.dataset.currency || '$';
    this.vehicles = [];
    this.plans = [];
    this.totalSteps = 7;

    this.state = {
      step: 0,
      postcode: '',
      stateCode: '',
      network: '',
      networkCode: '',
      tariff: 'single',
      vehicle: null,
      weeklyKm: parseFloat(this.dataset.defaultKm) || 210,
      charger: 2.4,
      chargerKind: 'Powerpoint',
      pref: 'overnight',
      currentRate: parseFloat(this.dataset.defaultRate) || 0.41,
      hasSolar: false,
      solarExport: parseFloat(this.dataset.defaultExport) || 10,
      feedIn: parseFloat(this.dataset.defaultFit) || 0.05,
      selectedPlanIdx: 0,
    };

    this.q = (sel) => this.querySelector(sel);
    this.byData = (name) => this.querySelector(`[data-evc="${name}"]`);
  }

  connectedCallback() {
    this.cacheEls();
    this.bind();
    this.applyDefaults();
    this.load();
    this.showStep(0);
  }

  cacheEls() {
    this.wizard = this.q('[data-evc-wizard]');
    this.results = this.q('[data-evc-results]');
    this.steps = [...this.querySelectorAll('[data-evc-step]')];
    this.progressSegs = [...this.querySelectorAll('.evc-progress__seg')];
    this.backBtn = this.q('[data-evc-back]');
    this.nextBtn = this.q('[data-evc-next]');
    this.nextLabel = this.q('[data-evc-next-label]');
  }

  applyDefaults() {
    this.byData('km-num').value = this.state.weeklyKm;
    this.byData('km-range').value = this.state.weeklyKm;
    this.byData('current-rate').value = this.state.currentRate;
    this.byData('solar-export').value = this.state.solarExport;
    this.byData('feed-in').value = this.state.feedIn;
    this.updateKwhNote();
    this.selectChoice('tariff', this.state.tariff);
    this.selectChoice('pref', this.state.pref);
    this.selectCharger(this.state.charger, this.state.chargerKind);
  }

  bind() {
    this.nextBtn.addEventListener('click', () => this.next());
    this.backBtn.addEventListener('click', () => this.back());
    this.q('[data-evc-edit]').addEventListener('click', () => this.editInputs());

    // postcode + suburb autocomplete
    this.setupPostcode();

    // choice groups
    this.querySelectorAll('[data-evc-group]').forEach((group) => {
      const name = group.dataset.evcGroup;
      group.querySelectorAll('.evc-choice').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (name === 'charger') this.selectCharger(parseFloat(btn.dataset.value), btn.dataset.kind, btn);
          else {
            this.state[name] = btn.dataset.value;
            this.markChoice(group, btn);
          }
          this.updateNav();
        });
      });
    });

    // vehicle selects
    this.byData('make').addEventListener('change', () => { this.fillModels(); this.fillVariants(); this.updateVehicleCard(); });
    this.byData('model').addEventListener('change', () => { this.fillVariants(); this.updateVehicleCard(); });
    this.byData('variant').addEventListener('change', () => this.updateVehicleCard());

    // distance
    const num = this.byData('km-num');
    const range = this.byData('km-range');
    num.addEventListener('input', () => { range.value = num.value; this.state.weeklyKm = parseFloat(num.value) || 0; this.updateKwhNote(); });
    range.addEventListener('input', () => { num.value = range.value; this.state.weeklyKm = parseFloat(range.value) || 0; this.updateKwhNote(); });

    // current rate
    this.byData('current-rate').addEventListener('input', (e) => { this.state.currentRate = parseFloat(e.target.value) || 0; });

    // solar
    this.byData('solar-toggle').addEventListener('click', (e) => {
      this.state.hasSolar = !this.state.hasSolar;
      e.currentTarget.setAttribute('aria-checked', String(this.state.hasSolar));
      e.currentTarget.classList.toggle('evc-switch--on', this.state.hasSolar);
      this.byData('solar-fields').hidden = !this.state.hasSolar;
    });
    this.byData('solar-export').addEventListener('input', (e) => { this.state.solarExport = parseFloat(e.target.value) || 0; });
    this.byData('feed-in').addEventListener('input', (e) => { this.state.feedIn = parseFloat(e.target.value) || 0; });
  }

  // ---- postcode / suburb autocomplete ----
  setupPostcode() {
    this.pcInput = this.byData('postcode');
    this.pcSuggest = this.byData('pc-suggest');
    this.postcodes = [];
    this.postcodeLookup = {};
    this.pcActiveIdx = -1;
    if (this.dataset.postcodesUrl) {
      fetch(this.dataset.postcodesUrl)
        .then((r) => r.json())
        .then((d) => { this.postcodes = Array.isArray(d) ? d : []; })
        .catch(() => { this.postcodes = []; });
    }
    if (this.dataset.postcodeLookupUrl) {
      fetch(this.dataset.postcodeLookupUrl)
        .then((r) => r.json())
        .then((d) => {
          this.postcodeLookup = d && typeof d === 'object' ? d : {};
          if (this.state.postcode.length === 4) this.applyPostcode(this.state.postcode, this.state.stateCode);
        })
        .catch(() => { this.postcodeLookup = {}; });
    }
    this.pcInput.addEventListener('input', () => this.onPostcodeInput());
    this.pcInput.addEventListener('keydown', (e) => this.onPostcodeKey(e));
    this.pcInput.addEventListener('blur', () => setTimeout(() => this.hidePcSuggest(), 150));
    this.pcInput.addEventListener('focus', () => { if (this.pcInput.value.trim()) this.onPostcodeInput(); });
  }

  onPostcodeInput() {
    let val = this.pcInput.value;
    const numeric = /^[0-9]*$/.test(val.trim());
    if (numeric) {
      val = val.replace(/\D/g, '').slice(0, 4);
      this.pcInput.value = val;
    }
    this.renderPcSuggest(val);
    if (numeric && val.length === 4) {
      this.applyPostcode(val, null);
    } else {
      this.state.postcode = '';
      this.state.stateCode = '';
      this.state.network = '';
      this.state.networkCode = '';
      this.setNetworkHint('Enter your postcode or suburb to detect your network.', false);
      this.updateNav();
    }
  }

  renderPcSuggest(q) {
    const box = this.pcSuggest;
    this.pcActiveIdx = -1;
    q = (q || '').trim().toLowerCase();
    if (!q || !this.postcodes.length) return this.hidePcSuggest();
    const numeric = /^[0-9]/.test(q);
    let matches;
    if (numeric) {
      matches = this.postcodes.filter((r) => r[1].startsWith(q)).slice(0, 8);
    } else {
      matches = this.postcodes.filter((r) => r[0].toLowerCase().startsWith(q)).slice(0, 8);
      if (matches.length < 8) {
        const more = this.postcodes.filter((r) => {
          const s = r[0].toLowerCase();
          return !s.startsWith(q) && s.includes(q);
        });
        matches = matches.concat(more).slice(0, 8);
      }
    }
    if (!matches.length) return this.hidePcSuggest();
    box.replaceChildren(
      ...matches.map((r) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'evc-pc-suggest__item';
        item.setAttribute('role', 'option');
        item.innerHTML = `<span class="evc-pc-suggest__suburb">${this.esc(r[0])}</span><span class="evc-pc-suggest__meta">${r[2]} ${r[1]}</span>`;
        item.addEventListener('mousedown', (e) => { e.preventDefault(); this.selectPc(r); });
        return item;
      })
    );
    box.hidden = false;
  }

  hidePcSuggest() {
    if (this.pcSuggest) { this.pcSuggest.hidden = true; this.pcSuggest.replaceChildren(); }
  }

  onPostcodeKey(e) {
    const box = this.pcSuggest;
    if (box.hidden) return;
    const items = [...box.querySelectorAll('.evc-pc-suggest__item')];
    if (!items.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.pcActiveIdx += e.key === 'ArrowDown' ? 1 : -1;
      if (this.pcActiveIdx < 0) this.pcActiveIdx = items.length - 1;
      if (this.pcActiveIdx >= items.length) this.pcActiveIdx = 0;
      items.forEach((it, i) => it.classList.toggle('evc-pc-suggest__item--active', i === this.pcActiveIdx));
    } else if (e.key === 'Enter' && this.pcActiveIdx >= 0) {
      e.preventDefault();
      items[this.pcActiveIdx].dispatchEvent(new MouseEvent('mousedown'));
    } else if (e.key === 'Escape') {
      this.hidePcSuggest();
    }
  }

  selectPc(r) {
    this.pcInput.value = r[1];
    this.hidePcSuggest();
    this.applyPostcode(r[1], r[2]);
  }

  applyPostcode(pc, stateOverride) {
    this.state.postcode = pc;
    this.state.stateCode = stateOverride || stateFromPostcode(pc);
    const looked = this.postcodeLookup[pc];
    const distributor = looked && DISTRIBUTOR_NAMES[looked.distributor];
    this.state.networkCode = distributor ? looked.distributor : '';
    this.state.network = distributor || (this.state.stateCode ? networkFor(pc, this.state.stateCode) : '');
    if (pc.length === 4 && this.state.stateCode) {
      this.setNetworkHint(`${this.state.network} network · ${STATE_NAMES[this.state.stateCode]}`, true);
    } else {
      this.setNetworkHint('Postcode not recognised.', false);
    }
    this.updateNav();
  }

  setNetworkHint(text, ok) {
    const hint = this.byData('network-hint');
    hint.textContent = text;
    hint.classList.toggle('evc-hint--ok', !!ok);
  }

  // ---- data load ----
  async load() {
    try {
      this.vehicles = await fetch(this.dataset.vehiclesUrl).then((r) => r.json());
    } catch (e) {
      this.vehicles = [];
    }
    // Prefer plans entered as section blocks (theme customizer); else the bundled asset.
    const embedded = readEmbeddedPlans(this);
    if (embedded) {
      this.plans = embedded.map(parsePlan);
    } else {
      try {
        this.plans = await fetch(this.dataset.plansUrl).then((r) => r.json());
      } catch (e) {
        this.plans = [];
      }
    }
    // Optional external feed (theme customizer "Retailer plans feed URL") — appended at the bottom.
    if (this.dataset.retailerPlansUrl) {
      try {
        this.plans = this.plans.concat(await fetchExternalPlans(this.dataset.retailerPlansUrl));
      } catch (e) {
        // keep the plans already loaded above
      }
    }
    this.fillMakes();
  }

  // ---- vehicle selects ----
  static opt(value, label) { const o = document.createElement('option'); o.value = value; o.textContent = label; return o; }

  fillMakes() {
    const makes = [...new Set(this.vehicles.map((v) => v.mk))].sort((a, b) => a.localeCompare(b));
    const sel = this.byData('make');
    sel.replaceChildren(EvEnergyCompare.opt('', 'Select manufacturer'), ...makes.map((m) => EvEnergyCompare.opt(m, m)));
  }

  fillModels() {
    const make = this.byData('make').value;
    const modelSel = this.byData('model');
    const models = [...new Set(this.vehicles.filter((v) => v.mk === make).map((v) => v.md))].sort((a, b) => a.localeCompare(b));
    modelSel.replaceChildren(EvEnergyCompare.opt('', 'Select model'), ...models.map((m) => EvEnergyCompare.opt(m, m)));
    modelSel.disabled = !make;
  }

  fillVariants() {
    const make = this.byData('make').value;
    const model = this.byData('model').value;
    this._variants = this.vehicles.filter((v) => v.mk === make && v.md === model);
    const sel = this.byData('variant');
    sel.replaceChildren(...this._variants.map((v, i) => EvEnergyCompare.opt(String(i), v.tr)));
    this.q('[data-evc-variant-wrap]').hidden = this._variants.length <= 1 || !model;
  }

  currentVehicle() {
    if (!this._variants || !this._variants.length) return null;
    const idx = parseInt(this.byData('variant').value, 10) || 0;
    return this._variants[Math.min(idx, this._variants.length - 1)] || this._variants[0];
  }

  updateVehicleCard() {
    const v = this.currentVehicle();
    const card = this.byData('vehicle-card');
    if (!v || !this.byData('model').value) { this.state.vehicle = null; card.hidden = true; this.updateNav(); return; }
    this.state.vehicle = v;
    // Real WLTP figures where available, else derived from the section efficiency.
    const range = v.rng != null ? v.rng : Math.round(v.bt * this.efficiency);
    const whkm = v.cons != null ? v.cons : Math.round(1000 / this.efficiency);
    this.byData('vehicle-name').textContent = `${v.mk} ${v.md}${this._variants.length > 1 ? ' ' + v.tr : ''}`;
    this.byData('vehicle-meta').textContent = `${range} km range · ${whkm} Wh/km · ${this.fmtKwh(v.bt)} kWh`;
    card.hidden = false;
    this.updateNav();
  }

  // Efficiency (km per kWh) for the selected vehicle, falling back to the section default.
  vehEff() {
    const v = this.state.vehicle;
    return v && v.cons ? 1000 / v.cons : this.efficiency;
  }

  // ---- choice helpers ----
  markChoice(group, btn) {
    group.querySelectorAll('.evc-choice').forEach((b) => b.classList.toggle('evc-choice--on', b === btn));
  }
  selectChoice(name, value) {
    const group = this.querySelector(`[data-evc-group="${name}"]`);
    if (!group) return;
    const btn = group.querySelector(`.evc-choice[data-value="${value}"]`);
    if (btn) this.markChoice(group, btn);
  }
  selectCharger(kw, kind, btn) {
    this.state.charger = kw;
    this.state.chargerKind = kind;
    const group = this.querySelector('[data-evc-group="charger"]');
    // Highlight every option with the same kW (e.g. the 7.2 kW in both groups),
    // since charger power is what drives the calculation.
    group.querySelectorAll('.evc-choice').forEach((b) => {
      b.classList.toggle('evc-choice--on', parseFloat(b.dataset.value) === kw);
    });
  }

  // ---- formatting ----
  fmtKwh(n) { return Number.isInteger(n) ? String(n) : n.toFixed(1); }
  money(n) { return `${this.currency}${n.toFixed(2)}`; }
  cents(rate) { return `${(rate * 100).toFixed(1)}c/kWh`; }

  updateKwhNote() {
    const kwh = this.state.weeklyKm / this.vehEff();
    this.byData('km-kwh').textContent = `Approximately ${kwh.toFixed(1)} kWh per week`;
  }

  // ---- navigation ----
  canProceed(step) {
    if (step === 0) return this.state.postcode.length === 4 && !!this.state.stateCode;
    if (step === 1) return !!this.state.vehicle;
    return true;
  }
  updateNav() {
    this.nextBtn.disabled = !this.canProceed(this.state.step);
    this.backBtn.hidden = this.state.step === 0;
    this.nextLabel.textContent = this.state.step === this.totalSteps - 1 ? 'See results' : 'Proceed';
  }
  showStep(i) {
    this.state.step = i;
    this.steps.forEach((s) => { s.hidden = parseInt(s.dataset.evcStep, 10) !== i; });
    this.progressSegs.forEach((seg, idx) => seg.classList.toggle('evc-progress__seg--on', idx <= i));
    this.updateNav();
  }
  next() {
    if (!this.canProceed(this.state.step)) return;
    if (this.state.step === this.totalSteps - 1) { this.showResults(); return; }
    this.showStep(this.state.step + 1);
  }
  back() { if (this.state.step > 0) this.showStep(this.state.step - 1); }
  editInputs() { this.results.hidden = true; this.wizard.hidden = false; this.showStep(this.state.step); }

  // ---- the engine ----
  computePlan(plan, weeklyKwh) {
    const charger = this.state.charger;
    const pref = this.state.pref;
    const prefFit = pref === 'anytime' ? true : pref === 'overnight' ? !!plan.overnight : !!plan.daytime;
    // Only credit the cheap hours that line up with when the user actually charges.
    // A plan with no defined window is assumed to apply whenever you charge.
    let usableHours;
    if (plan.cheapHoursPerWeek == null) usableHours = null;
    else if (pref === 'anytime') usableHours = plan.cheapHoursPerWeek;
    else if (pref === 'overnight') usableHours = plan.overnightHours || 0;
    else usableHours = plan.daytimeHours || 0;
    const capKwh = usableHours == null ? weeklyKwh : usableHours * charger;
    const cheapKwh = Math.min(weeklyKwh, capKwh);
    const overflow = Math.max(0, weeklyKwh - cheapKwh);
    let weekly = null;
    if (plan.cheapRate != null) {
      if (this.state.hasSolar && plan.daytime) {
        const solarKwh = Math.min(cheapKwh, this.state.solarExport * 7);
        weekly = solarKwh * this.state.feedIn + (cheapKwh - solarKwh) * plan.cheapRate + overflow * this.state.currentRate;
      } else {
        weekly = cheapKwh * plan.cheapRate + overflow * this.state.currentRate;
      }
    }
    const utilisation = weeklyKwh > 0 ? Math.min(100, (cheapKwh / weeklyKwh) * 100) : 0;
    const tags = [];
    if (plan.overnight) tags.push('Best for overnight');
    if (plan.daytime) tags.push('Best for daytime');
    if (utilisation >= 95) tags.push('High cheap window fit');
    return { plan, prefFit, cheapKwh, weekly, utilisation, usableHours, tags };
  }

  results_compute() {
    const weeklyKwh = this.state.weeklyKm / this.vehEff();
    const currentWeek = weeklyKwh * this.state.currentRate;
    const currentYear = currentWeek * 52;
    // Plans tied to a specific distributor only apply when it matches the user's detected network
    // (some retailers publish the same plan once per distributor, at a different rate each time).
    const inState = this.plans.filter(
      (p) =>
        (!p.states.length || p.states.includes(this.state.stateCode)) &&
        (!p.distributorCode || !this.state.networkCode || p.distributorCode === this.state.networkCode)
    );
    const evaluated = inState.map((p) => this.computePlan(p, weeklyKwh));
    const rankable = evaluated.filter((e) => e.weekly != null);
    rankable.sort((a, b) => a.weekly - b.weekly);
    const prefPool = rankable.filter((e) => e.prefFit);
    const best = (prefPool.length ? prefPool : rankable)[0] || null;
    return { weeklyKwh, currentWeek, currentYear, rankable, best };
  }

  showResults() {
    const r = this.results_compute();
    this._compute = r;
    this.wizard.hidden = true;
    this.results.hidden = false;

    this.byData('cur-week').textContent = this.money(r.currentWeek);
    this.byData('cur-year').textContent = `${this.money(r.currentYear)} /year`;
    this.byData('cur-rate-note').textContent = `Assumed current charge rate: ${this.money(this.state.currentRate)}/kWh`;

    if (r.best) {
      const b = r.best;
      const yr = b.weekly * 52;
      this.byData('best-name').textContent = `${b.plan.provider} – ${b.plan.plan}`;
      this.byData('best-week').textContent = this.money(b.weekly);
      this.byData('best-year').textContent = `${this.money(yr)} /year`;
      this.byData('best-rate-note').textContent = b.plan.isFree ? 'Free charging in window' : `Cheap charge rate: ${this.cents(b.plan.cheapRate)}`;
      const saving = r.currentYear - yr;
      this.byData('best-saving').textContent = saving > 0 ? `Potential saving of ${this.money(saving)} /year` : 'No saving vs your current rate';
    } else {
      this.byData('best-name').textContent = 'No matching plan found for your state';
      ['best-week', 'best-year', 'best-rate-note', 'best-saving'].forEach((k) => (this.byData(k).textContent = ''));
    }

    this.state.selectedPlanIdx = 0;
    this.renderFeasibility(r.best);
    this.renderPlanList(r);
  }

  renderFeasibility(entry) {
    const r = this._compute;
    const hoursNeeded = this.state.charger > 0 ? r.weeklyKwh / this.state.charger : 0;
    const cheapHours = entry ? entry.usableHours : null;
    const util = entry ? entry.utilisation : 0;
    this.byData('hours-needed').textContent = hoursNeeded.toFixed(1);
    this.byData('hours-cheap').textContent = cheapHours == null ? '—' : String(cheapHours);
    this.byData('util-pct').textContent = `${Math.round(util)}%`;
    this.byData('util-fill').style.width = `${Math.round(util)}%`;
    if (entry) {
      const prefWord = { overnight: 'overnight', daytime: 'during the day', anytime: 'anytime' }[this.state.pref];
      this.byData('bestfit-text').textContent =
        `This is the best match for you. You mostly charge ${prefWord} and require about ${hoursNeeded.toFixed(1)} hours per week. ${Math.round(util)}% of your charging fits within cheap windows.`;
    } else {
      this.byData('bestfit-text').textContent = 'No eligible plan was found for your location.';
    }
  }

  renderPlanList(r) {
    const list = this.byData('plan-list');
    list.replaceChildren();
    r.rankable.forEach((e, i) => {
      const yr = e.weekly * 52;
      const saving = r.currentYear - yr;
      const isBest = r.best && e.plan === r.best.plan;
      const card = document.createElement('div');
      card.className = 'evc-plan' + (i === this.state.selectedPlanIdx ? ' evc-plan--selected' : '') + (isBest ? ' evc-plan--best' : '');
      card.innerHTML = `
        <div class="evc-plan__head">
          <div>
            <span class="evc-plan__name">${this.esc(e.plan.plan)}</span>
            <span class="evc-plan__provider">${this.esc(e.plan.provider)}</span>
          </div>
          ${isBest ? '<span class="evc-plan__badge">★ Recommended</span>' : ''}
        </div>
        <div class="evc-plan__figs">
          <div><span class="evc-plan__fig">${this.money(e.weekly)}</span><span class="evc-plan__per">/week</span></div>
          <div><span class="evc-plan__fig">${this.money(yr)}</span><span class="evc-plan__per">/year</span></div>
          <div><span class="evc-plan__fig evc-plan__fig--save">${saving > 0 ? this.money(saving) : '—'}</span><span class="evc-plan__per">saving /year</span></div>
        </div>
        <div class="evc-plan__meta">
          ${e.plan.isFree ? 'Free in window' : 'Cheap rate: ' + this.cents(e.plan.cheapRate)} · Utilisation: ${Math.round(e.utilisation)}%
          ${e.plan.link ? `<a class="evc-plan__link" href="${this.esc(e.plan.link)}" target="_blank" rel="nofollow noopener">View plan ↗</a>` : ''}
        </div>
        <div class="evc-plan__tags">${e.tags.map((t) => `<span class="evc-tag">${this.esc(t)}</span>`).join('')}</div>
      `;
      card.addEventListener('click', (ev) => {
        if (ev.target.closest('a')) return;
        this.state.selectedPlanIdx = i;
        list.querySelectorAll('.evc-plan').forEach((c, ci) => c.classList.toggle('evc-plan--selected', ci === i));
        this.renderFeasibility(e);
      });
      list.appendChild(card);
    });
    if (!r.rankable.length) {
      const p = document.createElement('p');
      p.className = 'evc-muted';
      p.textContent = 'No comparable plans found for your state.';
      list.appendChild(p);
    }
  }

  esc(s) { return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
}

customElements.define('ev-energy-compare', EvEnergyCompare);

/* ------------------------------------------------------------------ */
/* Plans comparison table (independent, searchable)                   */
/* ------------------------------------------------------------------ */
class EvPlansTable extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-evc="table-body"]');
    this.empty = this.querySelector('[data-evc="table-empty"]');
    this.search = this.querySelector('[data-evc="table-search"]');
    this.plans = [];
    this.search.addEventListener('input', () => this.render());
    this.load();
  }
  async load() {
    const embedded = readEmbeddedPlans(this);
    if (embedded) {
      this.plans = embedded;
    } else {
      try { this.plans = await fetch(this.dataset.plansUrl).then((r) => r.json()); }
      catch (e) { this.plans = []; }
    }
    // Optional external feed (theme customizer "Retailer plans feed URL") — appended at the bottom.
    if (this.dataset.retailerPlansUrl) {
      try {
        this.plans = this.plans.concat(await fetchExternalPlansRaw(this.dataset.retailerPlansUrl));
      } catch (e) {
        // keep the plans already loaded above
      }
    }
    this.render();
  }
  esc(s) { return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
  render() {
    const term = (this.search.value || '').trim().toLowerCase();
    const rows = this.plans.filter((p) => {
      if (!term) return true;
      return [p.provider, p.plan, p.rate, p.window, p.fit, p.features, p.availability, p.distributor].join(' ').toLowerCase().includes(term);
    });
    this.body.replaceChildren();
    rows.forEach((p) => {
      const tr = document.createElement('tr');
      const provider = p.link ? `<a href="${this.esc(p.link)}" target="_blank" rel="nofollow noopener">${this.esc(p.provider)}</a>` : this.esc(p.provider);
      tr.innerHTML = `
        <td data-th="Provider">${provider}</td>
        <td data-th="Plan">${this.esc(p.plan)}</td>
        <td data-th="Charging rate"><strong>${this.esc(p.rate || '—')}</strong></td>
        <td data-th="Charging window">${this.esc(p.window || '—')}</td>
        <td data-th="Feed-in">${this.esc(p.fit || '—')}</td>
        <td data-th="Requirements">${this.esc(p.features || '—')}</td>
        <td data-th="Availability">${this.esc(p.availability || '—')}</td>
      `;
      this.body.appendChild(tr);
    });
    this.empty.hidden = rows.length > 0;
  }
}
customElements.define('ev-plans-table', EvPlansTable);
